/**
 * MKS-339 — helpdesk settings: per-project request categories and canned
 * replies, both admin/PM-managed.
 *
 * Shaped after `SlaTiersAdminPage` — project picker on top, a card per
 * collection, a v7 `Modal` for the form, and the v7 `Modal` (never a native
 * `confirm()`, checker I-19) for a destructive confirmation.
 *
 * The route is gated by `AdminOrPMRoute`, which is a PRESENTATION gate: the
 * backend decides per project (admin, or that project's PM) and per canned
 * reply (admin for a global one). A PM who opens this page and picks a project
 * they do not run gets a 403 toast from the server, which is the honest
 * answer — the alternative, filtering the picker to projects the caller runs,
 * would need a "projects I am PM of" endpoint the page does not otherwise
 * need, and would hide an admin's own projects from them.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TopBar } from '../../components/ui/TopBar';
import { Card } from '../../components/ui/Card';
import { Btn } from '../../components/ui/Btn';
import { Pill } from '../../components/ui/Pill';
import { Modal } from '../../components/ui/Modal';
import { Field } from '../../components/ui/Field';
import { TextInput } from '../../components/ui/TextInput';
import { DropdownSelect } from '../../components/ui/DropdownSelect';
import { useToast } from '../../components/ui/Toast';
import { useProjects } from '../../hooks/useProjects';
import {
  useRequestCategories,
  useCreateRequestCategory,
  useUpdateRequestCategory,
  useReorderRequestCategories,
  useDeleteRequestCategory,
  useAllCannedReplies,
  useCreateCannedReply,
  useUpdateCannedReply,
  useDeleteCannedReply,
} from '../../hooks/useHelpdesk';
import type { RequestCategory, CannedReply } from '../../services/api/helpdesk';

function errorText(err: unknown, fallback: string): string {
  return (err as Error)?.message ?? fallback;
}

export function HelpdeskSettingsPage() {
  const toast = useToast();
  const projects = useProjects();
  const [searchParams, setSearchParams] = useSearchParams();

  // The selected project lives in the URL so the page is linkable from a
  // project's own surface, the same way SlaTiersAdminPage accepts ?projectId=.
  const rawProjectId = searchParams.get('projectId');
  const projectId =
    rawProjectId && /^\d+$/.test(rawProjectId) ? Number(rawProjectId) : undefined;

  function selectProject(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === '') next.delete('projectId');
    else next.set('projectId', value);
    setSearchParams(next);
  }

  // ---- categories -------------------------------------------------------
  const categories = useRequestCategories(projectId, true);
  const createCategory = useCreateRequestCategory();
  const updateCategory = useUpdateRequestCategory();
  const reorderCategories = useReorderRequestCategories();
  const deleteCategory = useDeleteRequestCategory();

  // Already sorted by the server (orderIndex, then name). Held in a named
  // const because the reorder below has to send the WHOLE list, in exactly the
  // order the rows are drawn in.
  const categoryList = categories.data ?? [];

  const [categoryForm, setCategoryForm] = useState<{
    open: boolean;
    editing: RequestCategory | null;
    name: string;
  }>({ open: false, editing: null, name: '' });
  const [categoryToDelete, setCategoryToDelete] = useState<RequestCategory | null>(null);

  function submitCategory() {
    if (!projectId) return;
    // No `orderIndex`: a new category is appended by the server (after the
    // current last one), and an existing one is moved with the arrows below.
    const input = { name: categoryForm.name.trim() };
    const done = () => {
      setCategoryForm({ open: false, editing: null, name: '' });
      toast('Category saved', 'success');
    };
    const onError = (err: unknown) =>
      toast(errorText(err, 'Could not save the category'), 'error');

    if (categoryForm.editing) {
      updateCategory.mutate(
        { projectId, categoryId: categoryForm.editing.id, input },
        { onSuccess: done, onError },
      );
    } else {
      createCategory.mutate({ projectId, input }, { onSuccess: done, onError });
    }
  }

  /**
   * Move one category one place up (-1) or down (+1). Sends the resulting
   * FULL order, which is also what repairs a project whose categories were all
   * created on index 0 before the server started appending.
   */
  function moveCategory(index: number, delta: -1 | 1) {
    if (!projectId) return;
    const target = index + delta;
    if (target < 0 || target >= categoryList.length) return;

    const ids = categoryList.map((c) => c.id);
    // Lift-and-reinsert rather than a destructured swap: under
    // `noUncheckedIndexedAccess` an indexed read is `number | undefined`, and
    // splice keeps the array free of holes by construction.
    const [moved] = ids.splice(index, 1);
    if (moved === undefined) return;
    ids.splice(target, 0, moved);

    reorderCategories.mutate(
      { projectId, categoryIds: ids },
      {
        onError: (err) => toast(errorText(err, 'Could not reorder the categories'), 'error'),
      },
    );
  }

  function toggleArchived(category: RequestCategory) {
    if (!projectId) return;
    updateCategory.mutate(
      { projectId, categoryId: category.id, input: { archived: !category.archived } },
      {
        onSuccess: () =>
          toast(category.archived ? 'Category restored' : 'Category archived', 'success'),
        onError: (err) => toast(errorText(err, 'Could not update the category'), 'error'),
      },
    );
  }

  function confirmDeleteCategory() {
    if (!projectId || !categoryToDelete) return;
    deleteCategory.mutate(
      { projectId, categoryId: categoryToDelete.id },
      {
        onSuccess: () => {
          toast('Category deleted', 'success');
          setCategoryToDelete(null);
        },
        onError: (err) => {
          // The backend refuses a category still in use (409 CATEGORY_IN_USE)
          // and names archiving as the alternative — surface its words.
          toast(errorText(err, 'Could not delete the category'), 'error');
          setCategoryToDelete(null);
        },
      },
    );
  }

  // ---- canned replies ---------------------------------------------------
  const replies = useAllCannedReplies();
  const createReply = useCreateCannedReply();
  const updateReply = useUpdateCannedReply();
  const deleteReply = useDeleteCannedReply();

  const [replyForm, setReplyForm] = useState<{
    open: boolean;
    editing: CannedReply | null;
    scope: string;
    title: string;
    body: string;
  }>({ open: false, editing: null, scope: '', title: '', body: '' });
  const [replyToDelete, setReplyToDelete] = useState<CannedReply | null>(null);

  function submitReply() {
    const done = () => {
      setReplyForm({ open: false, editing: null, scope: '', title: '', body: '' });
      toast('Canned reply saved', 'success');
    };
    const onError = (err: unknown) =>
      toast(errorText(err, 'Could not save the canned reply'), 'error');

    if (replyForm.editing) {
      // Scope is not editable — see the backend note on updateCannedReply.
      updateReply.mutate(
        {
          replyId: replyForm.editing.id,
          input: { title: replyForm.title.trim(), body: replyForm.body },
        },
        { onSuccess: done, onError },
      );
    } else {
      createReply.mutate(
        {
          projectId: replyForm.scope === '' ? null : Number(replyForm.scope),
          title: replyForm.title.trim(),
          body: replyForm.body,
        },
        { onSuccess: done, onError },
      );
    }
  }

  function confirmDeleteReply() {
    if (!replyToDelete) return;
    deleteReply.mutate(replyToDelete.id, {
      onSuccess: () => {
        toast('Canned reply deleted', 'success');
        setReplyToDelete(null);
      },
      onError: (err) => {
        toast(errorText(err, 'Could not delete the canned reply'), 'error');
        setReplyToDelete(null);
      },
    });
  }

  const projectName = (id: number | null) =>
    id == null
      ? 'Global'
      : ((projects.data ?? []).find((p) => p.id === id)?.name ?? `Project ${id}`);

  return (
    <>
      <TopBar
        title="Helpdesk settings"
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Helpdesk' }]}
      />
      <div className="p-6 max-w-5xl mx-auto space-y-4">
        <Card>
          <Field label="Project" hint="Request categories belong to one project.">
            <DropdownSelect
              aria-label="Project"
              value={projectId != null ? String(projectId) : ''}
              onChange={(e) => selectProject(e.target.value)}
            >
              <option value="">— Pick a project —</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </DropdownSelect>
          </Field>
        </Card>

        {projectId && (
          <Card
            title="Request categories"
            action={
              <Btn
                size="sm"
                variant="accent"
                onClick={() => setCategoryForm({ open: true, editing: null, name: '' })}
              >
                + Add category
              </Btn>
            }
          >
            {categories.isLoading ? (
              <div className="py-6 text-center text-ms-text-muted">Loading…</div>
            ) : categoryList.length === 0 ? (
              <div className="py-6 text-center text-ms-text-muted">
                No categories yet. Clients see no category field until this project has
                at least one.
              </div>
            ) : (
              <ul className="divide-y divide-ms-border">
                {categoryList.map((category, index) => (
                  <li
                    key={category.id}
                    className="flex items-center justify-between py-2"
                    data-testid={`category-row-${category.id}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-[12.5px] text-ms-text-strong">
                        {category.name}
                      </span>
                      {category.archived && (
                        <Pill bg="#F1F0ED" fg="#6B6B72">
                          Archived
                        </Pill>
                      )}
                    </span>
                    <span className="flex items-center gap-1">
                      {/* The order the client's picker and the inbox filter
                          show these in. Disabled at the ends rather than
                          hidden, so the row's controls never shift sideways. */}
                      <Btn
                        size="sm"
                        variant="ghost"
                        aria-label={`Move ${category.name} up`}
                        disabled={index === 0 || reorderCategories.isPending}
                        onClick={() => moveCategory(index, -1)}
                      >
                        ↑
                      </Btn>
                      <Btn
                        size="sm"
                        variant="ghost"
                        aria-label={`Move ${category.name} down`}
                        disabled={
                          index === categoryList.length - 1 || reorderCategories.isPending
                        }
                        onClick={() => moveCategory(index, 1)}
                      >
                        ↓
                      </Btn>
                      <Btn
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setCategoryForm({
                            open: true,
                            editing: category,
                            name: category.name,
                          })
                        }
                      >
                        Rename
                      </Btn>
                      <Btn size="sm" variant="ghost" onClick={() => toggleArchived(category)}>
                        {category.archived ? 'Restore' : 'Archive'}
                      </Btn>
                      <Btn
                        size="sm"
                        variant="ghost"
                        onClick={() => setCategoryToDelete(category)}
                      >
                        Delete
                      </Btn>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        <Card
          title="Canned replies"
          action={
            <Btn
              size="sm"
              variant="accent"
              onClick={() =>
                setReplyForm({
                  open: true,
                  editing: null,
                  scope: projectId != null ? String(projectId) : '',
                  title: '',
                  body: '',
                })
              }
            >
              + Add reply
            </Btn>
          }
        >
          <p className="text-[11px] text-ms-text-muted mb-3">
            Use <code>{'{requester_name}'}</code> and <code>{'{ticket_code}'}</code> in the
            body — they are filled in when the reply is inserted into a comment.
          </p>
          {replies.isLoading ? (
            <div className="py-6 text-center text-ms-text-muted">Loading…</div>
          ) : (replies.data ?? []).length === 0 ? (
            <div className="py-6 text-center text-ms-text-muted">No canned replies yet.</div>
          ) : (
            <ul className="divide-y divide-ms-border">
              {(replies.data ?? []).map((reply) => (
                <li
                  key={reply.id}
                  className="flex items-start justify-between py-2 gap-3"
                  data-testid={`canned-reply-row-${reply.id}`}
                >
                  <span className="min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="text-[12.5px] text-ms-text-strong">{reply.title}</span>
                      <Pill
                        bg={reply.projectId == null ? '#EAF1FB' : '#F1F0ED'}
                        fg={reply.projectId == null ? '#1C4E8A' : '#6B6B72'}
                      >
                        {projectName(reply.projectId)}
                      </Pill>
                    </span>
                    <span className="block text-[11.5px] text-ms-text-muted truncate">
                      {reply.body}
                    </span>
                  </span>
                  <span className="flex items-center gap-1 shrink-0">
                    <Btn
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setReplyForm({
                          open: true,
                          editing: reply,
                          scope: reply.projectId == null ? '' : String(reply.projectId),
                          title: reply.title,
                          body: reply.body,
                        })
                      }
                    >
                      Edit
                    </Btn>
                    <Btn size="sm" variant="ghost" onClick={() => setReplyToDelete(reply)}>
                      Delete
                    </Btn>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* ---- category form ---- */}
      <Modal
        open={categoryForm.open}
        onClose={() => setCategoryForm((f) => ({ ...f, open: false }))}
        title={categoryForm.editing ? 'Edit category' : 'Add category'}
        footer={
          <div className="flex justify-end gap-2">
            <Btn
              variant="secondary"
              onClick={() => setCategoryForm((f) => ({ ...f, open: false }))}
            >
              Cancel
            </Btn>
            <Btn
              variant="accent"
              disabled={categoryForm.name.trim().length === 0}
              onClick={submitCategory}
            >
              Save
            </Btn>
          </div>
        }
      >
        <div className="space-y-3">
          <Field
            label="Name"
            hint="Order is set with the ↑ / ↓ arrows in the list; a new category is added at the end."
          >
            <TextInput
              value={categoryForm.name}
              maxLength={80}
              placeholder="Bug, Question, Change request, Access…"
              onChange={(e) => setCategoryForm((f) => ({ ...f, name: e.target.value }))}
            />
          </Field>
        </div>
      </Modal>

      {/* ---- category delete confirm ---- */}
      <Modal
        open={categoryToDelete !== null}
        onClose={() => setCategoryToDelete(null)}
        title="Delete category"
        footer={
          <div className="flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => setCategoryToDelete(null)}>
              Cancel
            </Btn>
            <Btn variant="danger" onClick={confirmDeleteCategory}>
              Delete
            </Btn>
          </div>
        }
      >
        <p className="text-[12.5px] text-ms-text-secondary">
          Delete “{categoryToDelete?.name}”? A category already used by a request cannot
          be deleted — archive it instead, and it stays readable on those requests.
        </p>
      </Modal>

      {/* ---- canned reply form ---- */}
      <Modal
        open={replyForm.open}
        onClose={() => setReplyForm((f) => ({ ...f, open: false }))}
        title={replyForm.editing ? 'Edit canned reply' : 'Add canned reply'}
        width={640}
        footer={
          <div className="flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => setReplyForm((f) => ({ ...f, open: false }))}>
              Cancel
            </Btn>
            <Btn
              variant="accent"
              disabled={
                replyForm.title.trim().length === 0 || replyForm.body.trim().length === 0
              }
              onClick={submitReply}
            >
              Save
            </Btn>
          </div>
        }
      >
        <div className="space-y-3">
          <Field
            label="Scope"
            hint={
              replyForm.editing
                ? 'Scope cannot be changed — delete and re-create to move a reply.'
                : 'Global replies are offered on every project and are admin-only.'
            }
          >
            <DropdownSelect
              aria-label="Scope"
              value={replyForm.scope}
              disabled={replyForm.editing !== null}
              onChange={(e) => setReplyForm((f) => ({ ...f, scope: e.target.value }))}
            >
              <option value="">Global (every project)</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </DropdownSelect>
          </Field>
          <Field label="Title">
            <TextInput
              value={replyForm.title}
              maxLength={120}
              placeholder="Acknowledge receipt"
              onChange={(e) => setReplyForm((f) => ({ ...f, title: e.target.value }))}
            />
          </Field>
          <Field label="Body">
            <textarea
              aria-label="Body"
              value={replyForm.body}
              rows={8}
              maxLength={10000}
              placeholder={'Hi {requester_name},\n\nThanks for {ticket_code} — we are on it.'}
              onChange={(e) => setReplyForm((f) => ({ ...f, body: e.target.value }))}
              className="w-full px-3 py-2 bg-white rounded-md border border-ms-border text-[12.5px] text-ms-text-strong placeholder:text-ms-text-muted focus:outline-none focus:ring-2 focus:ring-ms-orange/40"
            />
          </Field>
        </div>
      </Modal>

      {/* ---- canned reply delete confirm ---- */}
      <Modal
        open={replyToDelete !== null}
        onClose={() => setReplyToDelete(null)}
        title="Delete canned reply"
        footer={
          <div className="flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => setReplyToDelete(null)}>
              Cancel
            </Btn>
            <Btn variant="danger" onClick={confirmDeleteReply}>
              Delete
            </Btn>
          </div>
        }
      >
        <p className="text-[12.5px] text-ms-text-secondary">
          Delete “{replyToDelete?.title}”? Comments already posted with it are unaffected.
        </p>
      </Modal>
    </>
  );
}
